import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { CatalogsProspectsState, ProspectsState, Prospect, ISelectedSection } from './prospects.types';
import {
    loadProspectCatalogs,
    loadProspects,
    updateAttention,
    updateCSAssignation,
    updateNoteProspect,
    updateRecommendation,
} from './prospects.thunks';

export const prospectsInitialState: ProspectsState = {
    list: [],
    selectedSection: {
        type: 1,
        closed: 0,
        typification: null,
        assigned: null,
    },
    catalogs: {
        attentions: [],
        typification: [],
        recommendations: [],
        customer_success: [],
    },
    loading: false,
    pagination: {
        items: 0,
        pages: 0,
        last_page: 0,
        current_page: 1,
    },
};

const formatCatalogs = (catalogs) => {
    const formattedCatalogs = { ...catalogs };
    Object.keys(formattedCatalogs).forEach((key) => {
        if (formattedCatalogs[key].length) {
            formattedCatalogs[key] = formattedCatalogs[key].map((item) => {
                let itemlabel = item.name;
                if (!item.name) itemlabel = item.fullname;
                return {
                    value: item.id,
                    label: itemlabel,
                };
            });
        }
    });
    return formattedCatalogs;
};

const prospectsSlice = createSlice({
    name: 'prospects',
    initialState: prospectsInitialState,
    reducers: {
        setPagination: (state, action: PayloadAction<any>) => {
            state.pagination = action.payload;
        },
        setSelectedSection: (state, action: PayloadAction<ISelectedSection>) => {
            state.selectedSection = { ...state.selectedSection, ...action.payload };
        },
        setLoading: (state, action: PayloadAction<boolean>) => {
            state.loading = action.payload;
        },
        setCatalogs: (state, action: PayloadAction<CatalogsProspectsState>) => {
            state.catalogs = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder.addCase(loadProspects.pending, (state, action) => {
            if (!action.meta.arg.withoutLoader) {
                state.loading = true;
                state.list = [];
            }
        });
        builder.addCase(loadProspects.fulfilled, (state, action) => {
            state.loading = false;
            state.list = action.payload.prospects;
            state.pagination = action.payload.pagination;
        });
        builder.addCase(loadProspects.rejected, (state) => {
            state.loading = false;
            state.list = [];
            state.pagination = prospectsInitialState.pagination;
        });
        builder.addCase(loadProspectCatalogs.pending, (state) => {
            state.loading = true;
        });
        builder.addCase(loadProspectCatalogs.fulfilled, (state, action) => {
            state.catalogs = formatCatalogs(action.payload);
        });
        builder.addCase(loadProspectCatalogs.rejected, (state) => {
            state.loading = false;
            state.catalogs = prospectsInitialState.catalogs;
        });

        builder.addCase(updateCSAssignation.fulfilled, (state, action) => {
            state.list = state.list.map((prospect: Prospect) => {
                if (prospect.id === action.meta.arg.prospect_id) {
                    return { ...prospect, assigned_user_id: action.meta.arg.assigned_id };
                }
                return prospect;
            });
        });
        builder.addCase(updateAttention.fulfilled, (state, action) => {
            state.list = state.list.map((prospect: Prospect) => {
                if (prospect.id === action.meta.arg.prospect_id) {
                    return { ...prospect, attention_id: action.meta.arg.attention_id };
                }
                return prospect;
            });
        });
        builder.addCase(updateRecommendation.fulfilled, (state, action) => {
            state.list = state.list.map((prospect: Prospect) => {
                if (prospect.id === action.meta.arg.prospect_id) {
                    return { ...prospect, recommendation_id: action.meta.arg.recommendation_id };
                }
                return prospect;
            });
        });

        builder.addCase(updateNoteProspect.fulfilled, (state, action) => {
            state.list = state.list.map((prospect: Prospect) => {
                if (prospect.id === action.meta.arg.prospect_id) {
                    return {
                        ...prospect,
                        note_updated_at: action.payload.note_updated_at,
                        note_updated_by_fullname: action.payload.note_updated_by_fullname,
                    };
                }
                return prospect;
            });
        });
    },
});

export const { setPagination, setSelectedSection } = prospectsSlice.actions;

export const selectProspects = (state: { prospects: ProspectsState }) => state.prospects;

export default prospectsSlice.reducer;
